import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Grid3x3, Images, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';

function GalleryModal({ images, initialIndex, onClose }) {
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'single'

  const goNext = () => setActiveIndex(prev => (prev + 1) % images.length);
  const goPrev = () => setActiveIndex(prev => (prev - 1 + images.length) % images.length);

  const openSingle = (i) => {
    setActiveIndex(i);
    setViewMode('single');
  };

  return (
    <div className="fixed inset-0 z-[200] bg-white dark:bg-[#1C1917] flex flex-col overflow-hidden" data-testid="photo-gallery-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-[#E7E5E4] dark:border-[#44403C] bg-white dark:bg-[#1C1917] sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 hover:bg-[#F5F4F0] dark:hover:bg-[#292524] rounded-full transition" data-testid="gallery-close-btn">
            <X className="w-5 h-5 text-[#1C1917] dark:text-white" />
          </button>
          <span className="text-sm font-medium text-[#1C1917] dark:text-white">
            {viewMode === 'single' ? `${activeIndex + 1} / ${images.length}` : `${images.length} fotos`}
          </span>
        </div>
        <div className="flex items-center gap-1 bg-[#F5F4F0] dark:bg-[#292524] rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition text-xs font-medium flex items-center gap-1 ${viewMode === 'grid' ? 'bg-white dark:bg-[#44403C] shadow-sm text-[#1C1917] dark:text-white' : 'text-[#78716C] hover:text-[#1C1917]'}`}
            data-testid="gallery-grid-view-btn"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Grilla
          </button>
          <button
            onClick={() => setViewMode('single')}
            className={`p-1.5 rounded-md transition text-xs font-medium flex items-center gap-1 ${viewMode === 'single' ? 'bg-white dark:bg-[#44403C] shadow-sm text-[#1C1917] dark:text-white' : 'text-[#78716C] hover:text-[#1C1917]'}`}
            data-testid="gallery-single-view-btn"
          >
            <Images className="w-3.5 h-3.5" /> Foto
          </button>
        </div>
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="gallery-grid-view">
          <div className="max-w-5xl mx-auto columns-2 md:columns-3 gap-3 space-y-3">
            {images.map((img, i) => (
              <div
                key={`grid-${img}`}
                className="break-inside-avoid cursor-pointer rounded-lg overflow-hidden group"
                onClick={() => openSingle(i)}
              >
                <img
                  src={img}
                  alt={`Foto ${i + 1}`}
                  className="w-full object-cover group-hover:scale-[1.02] transition-transform duration-200"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Single Photo View */}
      {viewMode === 'single' && (
        <div className="flex-1 flex flex-col bg-black/95">
          <div className="flex-1 flex items-center justify-center px-4 relative">
            {images.length > 1 && (
              <button onClick={goPrev} className="absolute left-4 p-2.5 bg-white/10 hover:bg-white/20 rounded-full transition z-10" data-testid="gallery-prev">
                <ChevronLeft className="w-6 h-6 text-white" />
              </button>
            )}
            <img
              src={images[activeIndex]}
              alt={`Foto ${activeIndex + 1}`}
              className="max-h-[70vh] max-w-full object-contain rounded-lg"
              data-testid="gallery-active-image"
            />
            {images.length > 1 && (
              <button onClick={goNext} className="absolute right-4 p-2.5 bg-white/10 hover:bg-white/20 rounded-full transition z-10" data-testid="gallery-next">
                <ChevronRight className="w-6 h-6 text-white" />
              </button>
            )}
          </div>
          {/* Thumbnails */}
          {images.length > 1 && (
            <div className="px-4 py-3 flex gap-1.5 overflow-x-auto justify-center max-w-4xl mx-auto">
              {images.map((img, i) => (
                <button
                  key={`thumb-${img}`}
                  onClick={() => setActiveIndex(i)}
                  className={`flex-shrink-0 rounded-md overflow-hidden border-2 transition ${i === activeIndex ? 'border-white' : 'border-transparent opacity-50 hover:opacity-80'}`}
                >
                  <img src={img} alt={`Miniatura ${i + 1}`} className="w-14 h-10 object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PropertyPhotoGallery({ property }) {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Collect all images
  const allImages = [];
  if (property.images?.length > 0) {
    property.images.forEach(img => {
      if (img.url) allImages.push(img.url);
    });
  }
  if (property.airbnb_photos?.length > 0) {
    property.airbnb_photos.forEach(url => {
      if (url && !allImages.includes(url)) allImages.push(url);
    });
  }
  if (allImages.length === 0 && property.image_url) {
    allImages.push(property.image_url);
  }

  if (allImages.length <= 1) {
    return (
      <div className="rounded-2xl overflow-hidden shadow-lg">
        <img
          src={allImages[0] || property.image_url || ''}
          alt={property.title}
          className="w-full h-[400px] object-cover"
          data-testid="property-main-image"
        />
      </div>
    );
  }

  const openGallery = (index) => {
    setGalleryIndex(index);
    setGalleryOpen(true);
  };

  return (
    <>
      <div className="rounded-2xl overflow-hidden shadow-lg relative">
        {/* Main grid: 1 large + up to 4 small */}
        <div className="grid grid-cols-4 grid-rows-2 gap-1 h-[400px]">
          <div
            className="col-span-2 row-span-2 cursor-pointer overflow-hidden"
            onClick={() => openGallery(0)}
          >
            <img src={allImages[0]} alt={property.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-testid="property-main-image" />
          </div>

          {allImages.slice(1, 5).map((img, i) => (
            <div key={`main-${img}`} className="cursor-pointer overflow-hidden" onClick={() => openGallery(i + 1)}>
              <img src={img} alt={`Foto ${i + 2}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
          ))}

          {allImages.length < 5 && Array.from({ length: 5 - Math.min(allImages.length, 5) }).map((_, i) => (
            <div key={`empty-${i}`} className="bg-gray-200 dark:bg-gray-700" />
          ))}
        </div>

        {/* "Show all photos" button - always visible when more than 1 image */}
        <Button
          variant="outline"
          size="sm"
          className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm hover:bg-white shadow-md text-xs"
          onClick={() => openGallery(0)}
          data-testid="show-all-photos-btn"
        >
          <Grid3x3 className="w-3.5 h-3.5 mr-1.5" />
          Mostrar las {allImages.length} fotos
        </Button>
      </div>

      {galleryOpen && (
        <GalleryModal
          images={allImages}
          initialIndex={galleryIndex}
          onClose={() => setGalleryOpen(false)}
        />
      )}
    </>
  );
}
